// THE QUANTITY-TRACKING FILTER DRIVES WHAT IS VISIBLE IN 3D.
//
// Owner, 2026-09-28: "filtering the tracked quantities, show hide elements from
// 3D view as a whole", alongside "selecting 3d elements in view should update
// the selection panel in real time". The two together give the page a model it
// did not have:
//
//     THE SELECTION decides what is in the PANEL.
//     THE FILTER decides what is VISIBLE.
//
// A HIDE, NOT AN ISOLATE, and the distinction is the whole design. An isolate is
// a statement about everything else, so two isolates fight: the page already
// isolates for "only selected", and a filter isolate would have to win or lose
// against it with no way to say which on screen. A hide COMPOSES - the visible
// set is everything minus the union of what a person hid by hand and what the
// filter hides - and it is the only version where turning the filter off
// restores exactly what they had.
//
// ViewerStateStore already had the precedence right and untested wiring for it:
// isHidden() asks the individual list FIRST and the rule second, so a hand-hide
// survives both the rule and its removal (loader/streaming/__tests__/
// hiddenRule.test.js proves that part). Nothing outside the store had ever set
// a hidden rule; this is its first caller.
//
// ================= THE HAZARD, AND WHY THIS IS A FUNCTION =================
// The rule has to say "hide everything EXCEPT the filter's matches", which is a
// COMPLEMENT, and a complement of a set you do not have yet is THE WHOLE MODEL.
// The filter's uids arrive from the server; between the filter changing and the
// answer landing, the honest set is unknown. Hiding everything for those few
// hundred milliseconds is a black viewport, and a black viewport during loading
// is indistinguishable from a broken model - the user reloads, and it happens
// again.
// So: NO SET, NO RULE. This returns null rather than a rule that hides
// everything, and the caller leaves the model as it was. Degrading to today's
// behaviour and saying so beats degrading to silence.

/**
 * A hidden-rule that hides everything the filter does NOT match.
 *
 * @param {{has:(uid:string,hash:string)=>boolean}|null} match the filter's
 *        membership rule - a FragmentIsolation built from the filter's resolved
 *        uids and fragment hashes.
 * @param {{count:number|null}} opts `count` is how many elements the filter
 *        matched, as the SERVER counted them. It is the guard: 0 or null means
 *        "not resolved", and a complement is refused.
 * @returns {{has:Function, describe:Function}|null} null when it must not apply.
 */
export function complementRule(match, { count = null } = {}) {
  if (!match || typeof match.has !== 'function') return null;
  // A filter that matched NOTHING is a filter nobody can see past: hiding the
  // whole model to represent "no results" tells a person their model is broken
  // rather than that their filter is empty. The page says "0 match" in words;
  // the viewer stays as it was.
  if (!count || Number(count) <= 0) return null;
  return {
    // The store asks this for every instance it draws, so it does the cheapest
    // thing that is correct: one delegated membership test, negated.
    has(uid, hash) { return !match.has(uid, hash); },
    // So the state is answerable from the console when somebody asks why
    // something is invisible - "which of the eight things hid this" has cost
    // whole evenings on this page.
    describe() { return `hiding everything outside the quantity tracking filter (${Number(count).toLocaleString()} visible)`; },
    isFilterVisibility: true,
    matched: Number(count),
  };
}

/**
 * HIDE EVERYTHING, as a rule that can say why.
 *
 * Owner, 2026-10-05, through the leader: once he has touched the ticks in the
 * Tracking quantities table, the view obeys them LITERALLY - including zero.
 * Unticking everything means he wants nothing shown and he gets nothing shown.
 *
 * complementRule cannot express this: it refuses a count of 0 on purpose,
 * because an empty MATCH means "your filter found nothing" and hiding the
 * model for that reads as a broken page. An empty SELECTION HE CREATED is a
 * different fact - a deliberate act outranks a derived one - so it gets its
 * own rule rather than a special case inside the other one.
 *
 * It carries `describe()` for the same reason complementRule does: "which of
 * the eight things hid this" has cost whole evenings on this page.
 */
export function hideEverythingRule(why = 'nothing is ticked') {
  return {
    has() { return true; },
    describe() { return `hiding everything: ${why}`; },
    isFilterVisibility: true,
    hidesEverything: true,
    matched: 0,
  };
}

/**
 * Should the filter drive visibility at all?
 *
 * Deliberately separate from building the rule, because "the filter is active"
 * and "we know what it matches" are different facts and conflating them is how
 * the blank-viewport bug would get written.
 */
export function filterDrivesVisibility({ filters = null, resolvedCount = null, enabled = true } = {}) {
  if (!enabled) return false;
  if (!filters) return false;
  return !!resolvedCount && Number(resolvedCount) > 0;
}
